// features/notifications/NotificationCard.tsx — one notification row (social + council).

import { useState } from 'react';
import styled from 'styled-components';
import { Avatar, Button, MetaRow, useToast } from '@/ui';
import { Icon } from '@/icons';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Notification } from '@/core/models/notification';
import { useFollowRequestActions } from '@/hooks';
import { metaFor, councilLabelFor, COUNCIL_KIND_LABEL, FALLBACK_COUNCIL_LABEL } from './meta';

export interface NotificationCardProps {
  notification: Notification;
  /** Called with the notification when the row is activated (click / Enter / Space). */
  onActivate: (n: Notification) => void;
  /** Compact = no avatar (used by the topbar bell peek). */
  compact?: boolean;
}

const Row = styled.button<{ $unread: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  padding: 12px 14px;
  border: none;
  /* Read rows stay opaque (surface) when glass is OFF so the animated backdrop
     can't bleed through the row; glass ON keeps them clear. Unread rows carry the
     glass highlight (opaque-when-off via the glassBg token) + the accent left rule. */
  background: ${({ theme, $unread }) =>
    $unread
      ? `color-mix(in srgb, ${theme.color.accentCyan} 12%, ${theme.color.glassBg})`
      : theme.cardsGlass
        ? 'transparent'
        : theme.color.surface};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};

`;

const IconChip = styled.span<{ $danger?: boolean }>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: ${({ theme }) => theme.radius.sm};
  /* Teal is the single functional accent; the glyph differentiates the type.
     Safety-red is reserved for genuine moderation / under-review signifiers. */
  color: ${({ theme, $danger }) => ($danger ? theme.color.safety : theme.color.accentCyan)};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const TextLine = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  & b { font-weight: 700; }
`;

const Snippet = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Time = styled.span`
  flex: none;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const VerifyMark = styled.span`
  display: inline-flex;
  vertical-align: middle;
  margin-left: 3px;
  color: ${({ theme }) => theme.color.accentCyan};
`;

// Non-button variant of Row — used only for follow_request, which needs real
// interactive Accept/Decline <button>s nested inside it. Buttons cannot nest
// inside a <button> (Row's usual element), so this row is a <div> with its own
// inner clickable area for "go to profile" instead (see ClickArea below).
const RowDiv = styled(Row).attrs({ as: 'div' })`
  cursor: default;

`;

const ClickArea = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
  min-width: 0;
  text-align: left;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  border-radius: ${({ theme }) => theme.radius.sm};


`;

const RequestActions = styled.div`
  flex: none;
  display: flex;
  gap: 6px;
`;

export function NotificationCard({ notification: n, onActivate, compact = false }: NotificationCardProps) {
  const unread = !n.read;

  if (n.type === 'council') {
    const knownCouncilLabel = n.kind && n.kind !== 'discussion' && n.kind !== 'network' ? COUNCIL_KIND_LABEL[n.kind] : undefined;
    const councilLabel = councilLabelFor(n.kind);
    // When the kind is unrecognised, the fallback label already reads as a
    // full sentence ("Council notification") — prefixing "Council
    // notification: " again would stutter, so use it alone in that case.
    const ariaLabel = knownCouncilLabel
      ? `Council notification: ${knownCouncilLabel}`
      : FALLBACK_COUNCIL_LABEL;
    return (
      <Row
        type="button"
        $unread={unread}
        data-unread={unread}
        onClick={() => onActivate(n)}
        aria-label={ariaLabel}
      >
        <IconChip $danger aria-hidden>
          <Icon name="shield" size={18} />
        </IconChip>
        <Body>
          <TextLine>
            <MetaRow>
              <b>{councilLabel}</b>
              {n.body}
            </MetaRow>
          </TextLine>
        </Body>
        <Time>{n.time}</Time>
      </Row>
    );
  }

  if (n.type === 'follow_request') {
    return <FollowRequestRow n={n} unread={unread} onActivate={onActivate} compact={compact} />;
  }
  if (n.kind === 'network') {
    return <Row type="button" $unread={unread} data-unread={unread} onClick={() => onActivate(n)} aria-label={n.body || 'TruXSocial update'}>
      <IconChip aria-hidden><Icon name="bell" size={18} /></IconChip>
      <Body><TextLine>{n.body || 'A workspace has an update.'}</TextLine></Body><Time>{n.time}</Time>
    </Row>;
  }

  const meta = n.kind === 'discussion' ? { icon: 'bell' as const, verb: `updated a discussion you follow, ${n.updateCount ?? 1} new replies` } : metaFor(n.type);
  const actor = n.actor;
  return (
    <Row
      type="button"
      $unread={unread}
      data-unread={unread}
      onClick={() => onActivate(n)}
      aria-label={`${actor?.name ?? 'Someone'} ${meta.verb}`}
    >
      <IconChip aria-hidden>
        <Icon name={meta.icon} size={18} />
      </IconChip>
      {!compact && actor && (
        <span data-testid="notif-avatar">
          <Avatar name={actor.name} size={40} />
        </span>
      )}
      <Body>
        <TextLine>
          <b>{actor?.name}</b>
          {actor?.verified && (
            <VerifyMark>
              <Icon name="verify" size={14} title="Verified account" />
            </VerifyMark>
          )}{' '}
          {meta.verb}
        </TextLine>
        {/* `body` is council's field, reused here for feedback's status-note /
            public-update text — the two types never populate both at once. */}
        {(n.snippet || n.body) && <Snippet>{n.snippet || n.body}</Snippet>}
      </Body>
      <Time>{n.time}</Time>
    </Row>
  );
}

// ── follow_request: inline Accept/Decline.
//
// Outcome only flips on a genuine 'ok' result — a failed call must NOT render
// as success with the buttons vanished (that was the bug: setOutcome ran
// unconditionally). 'handled' (404 — resolved elsewhere) settles the row the
// same as a real outcome, since there is nothing left to act on; any OTHER
// failure leaves the row on 'pending' with a distinct toast so the buttons
// stay and the user can retry.
type RequestOutcome = 'pending' | 'accepted' | 'declined' | 'handled';

function FollowRequestRow({
  n,
  unread,
  onActivate,
  compact,
}: {
  n: Notification;
  unread: boolean;
  onActivate: (n: Notification) => void;
  compact: boolean;
}) {
  const { accept, decline, refetchNotifications } = useFollowRequestActions();
  const toast = useToast();
  const [outcome, setOutcome] = useState<RequestOutcome>('pending');
  const [busy, setBusy] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const meta = metaFor(n.type);
  const actor = n.actor;
  const actorHandle = actor?.handle ?? '';

  function handleAccept() {
    if (busy || outcome !== 'pending') return;
    setBusy(true);
    void accept(actorHandle).then((result) => {
      setBusy(false);
      if (result === 'ok') {
        setOutcome('accepted');
        void refetchNotifications();
        // "Mark the notification read/activated on accept" — onActivate
        // already marks read (and, since this is an actor-driven type with
        // no postId, navigates to the accepted follower's profile).
        onActivate(n);
      } else if (result === 'handled') {
        setOutcome('handled');
        toast('This request was already handled');
      } else {
        toast('Could not accept. Please try again');
      }
    });
  }

  function runDecline() {
    setConfirmDecline(false);
    if (busy || outcome !== 'pending') return;
    setBusy(true);
    void decline(actorHandle).then((result) => {
      setBusy(false);
      if (result === 'ok') {
        setOutcome('declined');
        void refetchNotifications();
      } else if (result === 'handled') {
        setOutcome('handled');
        toast('This request was already handled');
      } else {
        toast('Could not decline. Please try again');
      }
    });
  }

  const settledText =
    outcome === 'accepted'
      ? 'follow request accepted'
      : outcome === 'declined'
        ? 'follow request declined'
        : outcome === 'handled'
          ? 'follow request already handled'
          : meta.verb;

  return (
    <RowDiv $unread={unread} data-unread={unread}>
      <ClickArea
        type="button"
        onClick={() => onActivate(n)}
        aria-label={`${actor?.name ?? 'Someone'} ${meta.verb}`}
      >
        <IconChip aria-hidden>
          <Icon name={meta.icon} size={18} />
        </IconChip>
        {!compact && actor && (
          <span data-testid="notif-avatar">
            <Avatar name={actor.name} size={40} />
          </span>
        )}
        <Body>
          <TextLine>
            <b>{actor?.name}</b>
            {actor?.verified && (
              <VerifyMark>
                <Icon name="verify" size={14} title="Verified account" />
              </VerifyMark>
            )}{' '}
            {settledText}
          </TextLine>
        </Body>
      </ClickArea>
      {outcome === 'pending' && (
        <RequestActions>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setConfirmDecline(true)}
            disabled={busy}
            aria-label={`Decline follow request from ${actorHandle}`}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleAccept}
            disabled={busy}
            aria-label={`Accept follow request from ${actorHandle}`}
          >
            Accept
          </Button>
        </RequestActions>
      )}
      <Time>{n.time}</Time>
      {confirmDecline && (
        <ConfirmDialog
          title="Decline follow request?"
          message={`Decline ${actorHandle}'s follow request? They won't be told, and they can request again.`}
          confirmLabel="Decline"
          onConfirm={runDecline}
          onClose={() => setConfirmDecline(false)}
        />
      )}
    </RowDiv>
  );
}
